import { Helpers } from '../helpers';
import { PaymentMethod, MemberSuffix, MemberTitle } from '../constants/enums';

export const useReportFormatters = () => {
  const formatDate = (date: Date | string | null): string => {
    if (!date) return '';
    return Helpers.toShortDate(date);
  };

  const formatDateFromUtc = (date: Date | string | null): string => {
    if (!date) return '';
    return Helpers.toShortDateFromUtc(date);
  };

  const formatDateTime = (date: Date | string | null): string => {
    if (!date) return '';
    return Helpers.toShortDateTime(date);
  };

  const formatNumber = (value: number | null, decimals: number = 2): string => {
    if (value === null || value === undefined) return '';
    return value.toFixed(decimals);
  };

  const formatYear = (date: Date | string | null): string => {
    if (!date) return '';
    return new Date(date).getFullYear().toString();
  };

  const formatCommittee = (committee: { description?: string } | null): string => {
    return committee?.description || 'Unknown';
  };

  const formatCurrency = (value: number | null): string => {
    if (value === null || value === undefined) return '';
    return Helpers.formatCurrency(value);
  };

  const formatPaymentMethod = (paymentMethod: PaymentMethod): string => {
    switch (paymentMethod) {
      case PaymentMethod.NONE:
        return 'None';
      case PaymentMethod.CASH:
        return 'Cash';
      case PaymentMethod.CHECK:
        return 'Check';
      case PaymentMethod.CREDIT_CARD:
        return 'Credit Card';
      case PaymentMethod.MONEY_ORDER:
        return 'Money Order';
      case PaymentMethod.WEBSITE:
        return 'Website';
      default:
        return 'Unknown';
    }
  };

  const formatSuffix = (suffix: MemberSuffix | number | string | null | undefined): string => {
    if (suffix === null || suffix === undefined) return '';

    // Handle string enum names (from API) or numeric enum values
    if (typeof suffix === 'string') {
      const upperSuffix = suffix.toUpperCase();
      // Handle enum name strings
      if (upperSuffix === 'NONE' || suffix === '') return '';
      if (upperSuffix === 'I') return 'I';
      if (upperSuffix === 'II') return 'II';
      if (upperSuffix === 'III') return 'III';
      if (upperSuffix === 'IV') return 'IV';
      if (upperSuffix === 'MD') return 'MD';
      if (upperSuffix === 'DR') return 'Dr.';
      if (upperSuffix === 'JR') return 'Jr.';
      if (upperSuffix === 'SR') return 'Sr.';

      // Try to parse as number if not an enum name
      const parsedValue = parseInt(suffix, 10);
      if (!isNaN(parsedValue)) {
        suffix = parsedValue;
      } else {
        return '';
      }
    }

    // Handle numeric enum values
    const suffixValue = suffix as number;

    switch (suffixValue) {
      case MemberSuffix.None:
        return '';
      case MemberSuffix.I:
        return 'I';
      case MemberSuffix.II:
        return 'II';
      case MemberSuffix.III:
        return 'III';
      case MemberSuffix.IV:
        return 'IV';
      case MemberSuffix.MD:
        return 'MD';
      case MemberSuffix.Jr:
        return 'Jr.';
      case MemberSuffix.Sr:
        return 'Sr.';
      default:
        return '';
    }
  };

  const formatTitle = (title: MemberTitle | number | string | null | undefined): string => {
    if (title === null || title === undefined) return '';

    // Handle string enum names (from API) or numeric enum values
    if (typeof title === 'string') {
      const upperTitle = title.toUpperCase();
      // Handle enum name strings
      if (upperTitle === 'NONE' || title === '') return '';
      if (upperTitle === 'MR') return 'Mr.';
      if (upperTitle === 'MRS') return 'Mrs.';
      if (upperTitle === 'MS') return 'Ms.';

      // Try to parse as number if not an enum name
      const parsedValue = parseInt(title, 10);
      if (!isNaN(parsedValue)) {
        title = parsedValue;
      } else {
        return '';
      }
    }

    // Handle numeric enum values
    const titleValue = title as number;

    switch (titleValue) {
      case MemberTitle.None:
        return '';
      case MemberTitle.Mr:
        return 'Mr.';
      case MemberTitle.Mrs:
        return 'Mrs.';
      case MemberTitle.Ms:
        return 'Ms.';
      default:
        return '';
    }
  };

  return {
    formatDate,
    formatDateFromUtc,
    formatDateTime,
    formatNumber,
    formatYear,
    formatCommittee,
    formatCurrency,
    formatPaymentMethod,
    formatSuffix,
    formatTitle
  };
};
